import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import { HERO_IMAGE } from "@/lib/data";

const Hero = () => {
  return (
    <section className="relative bg-sand pt-10 pb-16 lg:pt-16 lg:pb-24 overflow-hidden" data-testid="hero-section">
      {/* Decorative blurred amber blob */}
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-gold/30 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-sage/20 rounded-full blur-[140px] pointer-events-none" />

      <div className="relative container-tm grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* Left text */}
        <div className="lg:col-span-6 reveal">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-ink/10 shadow-soft mb-7">
            <span className="flex items-center gap-1 text-gold">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={11} fill="currentColor" strokeWidth={0} />
              ))}
            </span>
            <span className="text-[11px] font-sans uppercase tracking-[0.2em] font-semibold text-ink/70">
              4,9 / 5, Avis propriétaires
            </span>
          </div>

          <h1
            data-testid="hero-title"
            className="font-serif font-light text-ink text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[0.98] tracking-[-0.03em]"
          >
            La conciergerie
            <br />
            <span className="italic text-sage font-normal">premium</span> pour vos
            <br />
            locations <span className="italic text-gold font-normal">Airbnb</span>.
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-7 font-sans text-ink/70 text-lg md:text-xl max-w-xl leading-relaxed"
          >
            Maximisez vos revenus locatifs sans effort. Deux équipes locales (Côte d'Émeraude & Morbihan), réactives et disponibles 24h/24 et 7j/7, y compris la nuit.
          </p>

          {/* Formule 15 % TTC mise en avant */}
          <div
            data-testid="hero-pricing-highlight"
            className="mt-6 inline-flex items-center gap-3 pl-2 pr-5 py-2 rounded-full bg-ink text-white shadow-lift"
          >
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-gold text-ink font-serif text-base font-semibold">
              15%
            </span>
            <span className="font-sans text-sm leading-tight">
              <span className="font-semibold">Commission unique 15 % TTC.</span>{" "}
              <span className="text-white/70">Sans frais cachés, sans engagement.</span>
            </span>
          </div>

          <div className="mt-9 flex flex-wrap gap-3">
            <Link
              to="/contact"
              state={{ scrollTo: "simulator" }}
              className="btn-accent group"
              data-testid="hero-cta-estimate"
            >
              Calculez vos revenus
              <ArrowRight size={14} strokeWidth={2} className="ml-3 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/contact" className="btn-secondary" data-testid="hero-cta-contact">
              Nous contacter
            </Link>
          </div>

          {/* Mini trust strip */}
          <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
            <MiniStat value="78 942" label="Voyageurs accueillis" />
            <MiniStat value="98,7%" label="Satisfaction" />
            <MiniStat value="+30%" label="Revenus en plus" />
          </div>
        </div>

        {/* Right visual */}
        <div className="lg:col-span-6 relative">
          <div className="relative aspect-[4/5] lg:aspect-[5/6] rounded-[28px] overflow-hidden bg-ink shadow-lift">
            <img
              src={HERO_IMAGE}
              alt="Saint-Malo, Bretagne, Côte d'Émeraude"
              className="w-full h-full object-cover animate-slow-zoom"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />

            {/* Tag top-left */}
            <div className="absolute top-6 left-6 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur-sm shadow-soft">
              <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
              <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink">
                Côte d'Émeraude · Morbihan
              </span>
            </div>

            {/* Floating revenue card */}
            <div className="absolute bottom-6 left-6 right-6 lg:right-auto lg:max-w-[280px] bg-white rounded-2xl p-5 shadow-lift border border-ink/5">
              <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-sage">
                Revenu net mensuel
              </p>
              <p className="mt-2 font-serif text-4xl text-ink leading-none">
                1 957 <span className="text-gold">€</span>
              </p>
              <p className="mt-2 text-xs font-sans text-ink/55 leading-relaxed">
                Maison 4 pers., Bretagne · après commission TERRE ET MER (15%)
              </p>
            </div>
          </div>

          {/* Decorative card overlap */}
          <div className="hidden lg:flex absolute -top-6 -right-6 bg-ink text-white rounded-2xl p-5 shadow-lift items-center gap-4 max-w-[260px]">
            <div className="w-10 h-10 rounded-full bg-gold flex items-center justify-center shrink-0">
              <span className="font-serif text-base leading-none">24h</span>
            </div>
            <div>
              <p className="font-serif text-base leading-tight">Service 24h/24</p>
              <p className="text-[11px] font-sans text-white/60 mt-1">7j/7 · permanence de nuit</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const MiniStat = ({ value, label }) => (
  <div>
    <p className="font-serif text-2xl text-ink leading-none">{value}</p>
    <p className="mt-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-ink/55">
      {label}
    </p>
  </div>
);

export default Hero;
